import { useQuery } from "@tanstack/react-query";
import { useParams, Link, useLocation } from "wouter";
import { getArticleUrl } from "@/lib/article-url";
import { plainTextToHtml } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Calendar, Building2, User, ChevronLeft, ChevronRight, Newspaper, Tag, Lightbulb, Loader2, Share2, Link2, Check, ExternalLink, Mail, Phone, MessageSquare, Award, X, Play, Clock, FileText, ChevronDown, Target } from "lucide-react";
import { SiX, SiFacebook } from "react-icons/si";
import { FaLinkedin } from "react-icons/fa";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { VideoFacade, VideoFacadeSkeleton } from "@/components/video-facade";
import { useToast } from "@/hooks/use-toast";
import { useArticleEngagement } from "@/hooks/use-article-engagement";
import { format } from "date-fns";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { InsightProvider } from "@/components/insight-modal";
import { AddToShortListButton } from "@/components/add-to-short-list-button";
import { HighlightedHTMLContent } from "@/components/article-content-highlighter";
import { CtaPanel } from "@/components/cta-panel";
import { useState, useCallback, useEffect, type ReactNode } from "react";
import useEmblaCarousel from "embla-carousel-react";
import type { Article, Company, Person, MediaAsset, MediaVariant, ImageSizePreset } from "@shared/schema";

interface BrandTagInfo {
  slug: string;
  name: string;
  shortName: string;
  color: string;
  source: string;
  confidence: number | null;
}

interface ArticleWithRelations extends Article {
  companies?: Company[];
  people?: Person[];
  brandTags?: BrandTagInfo[];
}

interface GalleryImage {
  id: string;
  mediaAssetId: string;
  isHero: boolean;
  sortOrder: number;
  caption?: string | null;
  asset?: MediaAsset;
  variants?: Array<MediaVariant & { preset?: ImageSizePreset }>;
}

function ImageSlider({ images, getImageUrl }: { images: GalleryImage[], getImageUrl: (img: GalleryImage, preset?: string) => string }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: "start" });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScroll, setCanScroll] = useState(false);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const checkCanScroll = useCallback(() => {
    if (!emblaApi) return;
    const scrollSnaps = emblaApi.scrollSnapList();
    setCanScroll(scrollSnaps.length > 1);
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", checkCanScroll);
    emblaApi.on("resize", checkCanScroll);
    
    onSelect();
    checkCanScroll();
    
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", checkCanScroll);
      emblaApi.off("resize", checkCanScroll);
    };
  }, [emblaApi, onSelect, checkCanScroll]);

  if (images.length === 0) return null;

  return (
    <div className="my-8">
      <div className="flex items-center gap-3">
        <div className={`flex-shrink-0 ${canScroll ? 'visible' : 'invisible'}`}>
          <Button
            variant="outline"
            size="icon"
            onClick={scrollPrev}
            data-testid="button-slider-prev"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="flex-1 overflow-hidden rounded-lg" ref={emblaRef}>
          <div className="flex">
            {images.map((image, index) => (
              <Dialog key={image.id}>
                <DialogTrigger asChild>
                  <div 
                    className="flex-[0_0_100%] min-w-0 cursor-pointer md:flex-[0_0_50%] lg:flex-[0_0_33.333%] px-2"
                    data-testid={`slide-gallery-image-${index}`}
                  >
                    <div className="aspect-[4/3] overflow-hidden rounded-md border hover-elevate">
                      <img
                        src={getImageUrl(image, "gallery") || getImageUrl(image)}
                        alt={image.caption || image.asset?.alt || `Gallery image ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {image.caption && (
                      <p className="text-sm text-muted-foreground mt-2 text-center">{image.caption}</p>
                    )}
                  </div>
                </DialogTrigger>
                <DialogContent className="max-w-5xl p-0 border-0 bg-transparent">
                  <div className="relative">
                    <img
                      src={getImageUrl(image, "lightbox") || getImageUrl(image)}
                      alt={image.caption || image.asset?.alt || `Gallery image ${index + 1}`}
                      className="w-full h-auto rounded-lg"
                      data-testid="img-lightbox"
                    />
                    {image.caption && (
                      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent rounded-b-lg">
                        <p className="text-white text-sm">{image.caption}</p>
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            ))}
          </div>
        </div>
        
        <div className={`flex-shrink-0 ${canScroll ? 'visible' : 'invisible'}`}>
          <Button
            variant="outline"
            size="icon"
            onClick={scrollNext}
            data-testid="button-slider-next"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {canScroll && (
        <div className="flex justify-center gap-2 mt-4">
          {images.map((_, index) => (
            <button
              key={index}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === selectedIndex ? "bg-primary" : "bg-muted-foreground/30"
              }`}
              onClick={() => emblaApi?.scrollTo(index)}
              data-testid={`button-dot-${index}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ShareButtons({ title, url }: { title: string; url: string }) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}${url}` : url;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast({ title: "Link copied to clipboard" });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({ title: "Failed to copy link", variant: "destructive" });
    }
  };

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-muted-foreground flex items-center gap-1">
        <Share2 className="h-4 w-4" />
        Share:
      </span>
      <Button
        size="icon"
        variant="ghost"
        onClick={() => window.open(shareLinks.twitter, '_blank', 'width=600,height=400')}
        aria-label="Share on X"
        data-testid="button-share-twitter"
      >
        <SiX className="h-4 w-4" />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        onClick={() => window.open(shareLinks.linkedin, '_blank', 'width=600,height=400')}
        aria-label="Share on LinkedIn"
        data-testid="button-share-linkedin"
      >
        <FaLinkedin className="h-4 w-4" />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        onClick={() => window.open(shareLinks.facebook, '_blank', 'width=600,height=400')}
        aria-label="Share on Facebook"
        data-testid="button-share-facebook"
      >
        <SiFacebook className="h-4 w-4" />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        onClick={copyToClipboard}
        aria-label="Copy link"
        data-testid="button-copy-link"
      >
        {copied ? <Check className="h-4 w-4 text-green-500" /> : <Link2 className="h-4 w-4" />}
      </Button>
    </div>
  );
}

interface EnquiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  link: { label: string; url: string; companyName?: string };
  onProceed: () => void;
}

function EnquiryModal({ isOpen, onClose, link, onProceed }: EnquiryModalProps) {
  const [countdown, setCountdown] = useState(60);
  const [autoRedirect, setAutoRedirect] = useState(true);

  useEffect(() => {
    if (!isOpen) {
      setCountdown(60);
      return;
    }

    if (!autoRedirect) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onProceed();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, autoRedirect, onProceed]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="enquiry-modal">
        <DialogHeader>
          <DialogTitle>Visiting {link.companyName || link.label}</DialogTitle>
          <DialogDescription>
            You are about to leave FM World
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="bg-accent/50 rounded-md p-4">
            <div className="flex items-start gap-3">
              <Award className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-sm">Become a Sponsor</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Want premium placement for your company? Contact us about sponsorship opportunities.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-medium text-sm">Additional Enquiry Options</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button variant="outline" className="justify-start gap-2" data-testid="button-request-quote">
                <MessageSquare className="h-4 w-4" />
                Request a Quote
              </Button>
              <Button variant="outline" className="justify-start gap-2" data-testid="button-contact-supplier">
                <Mail className="h-4 w-4" />
                Contact Supplier
              </Button>
              <Button variant="outline" className="justify-start gap-2" data-testid="button-request-callback">
                <Phone className="h-4 w-4" />
                Request Callback
              </Button>
              <Button variant="outline" className="justify-start gap-2" data-testid="button-download-info">
                <Tag className="h-4 w-4" />
                Download Info Pack
              </Button>
            </div>
          </div>

          <Separator />

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-muted-foreground">
              {autoRedirect ? (
                <span>Redirecting in {countdown}s...</span>
              ) : (
                <span>Click proceed to continue</span>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAutoRedirect(!autoRedirect)}
            data-testid="button-toggle-redirect"
          >
            {autoRedirect ? "Cancel auto-redirect" : "Enable auto-redirect"}
          </Button>
          <Button onClick={onProceed} data-testid="button-proceed">
            <ExternalLink className="h-4 w-4 mr-2" />
            Proceed Now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface PersonEnquiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  person: { name: string; jobTitle?: string; linkedinUrl: string };
  onProceed: () => void;
}

function PersonEnquiryModal({ isOpen, onClose, person, onProceed }: PersonEnquiryModalProps) {
  const [countdown, setCountdown] = useState(60);
  const [autoRedirect, setAutoRedirect] = useState(true);

  useEffect(() => {
    if (!isOpen) {
      setCountdown(60);
      return;
    }

    if (!autoRedirect) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onProceed();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, autoRedirect, onProceed]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="person-enquiry-modal">
        <DialogHeader>
          <DialogTitle>Connect with {person.name}</DialogTitle>
          <DialogDescription>
            {person.jobTitle ? `${person.jobTitle} - ` : ''}View their LinkedIn profile
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="bg-accent/50 rounded-md p-4">
            <div className="flex items-start gap-3">
              <User className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-sm">Professional Connection</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Connect with industry professionals featured in our content.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-medium text-sm">Contact Options</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button variant="outline" className="justify-start gap-2" data-testid="button-request-intro">
                <MessageSquare className="h-4 w-4" />
                Request Introduction
              </Button>
              <Button variant="outline" className="justify-start gap-2" data-testid="button-book-meeting">
                <Calendar className="h-4 w-4" />
                Book a Meeting
              </Button>
              <Button variant="outline" className="justify-start gap-2" data-testid="button-send-message">
                <Mail className="h-4 w-4" />
                Send Message
              </Button>
              <Button variant="outline" className="justify-start gap-2" data-testid="button-request-cv">
                <User className="h-4 w-4" />
                Request Speaker Bio
              </Button>
            </div>
          </div>

          <Separator />

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-muted-foreground">
              {autoRedirect ? (
                <span>Opening LinkedIn in {countdown}s...</span>
              ) : (
                <span>Click to view profile</span>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAutoRedirect(!autoRedirect)}
            data-testid="button-toggle-person-redirect"
          >
            {autoRedirect ? "Cancel auto-redirect" : "Enable auto-redirect"}
          </Button>
          <Button onClick={onProceed} data-testid="button-view-linkedin">
            <FaLinkedin className="h-4 w-4 mr-2" />
            View LinkedIn Profile
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface VideoSchemaProps {
  article: {
    title: string;
    excerpt?: string | null;
    videoUrl?: string | null;
    videoThumbnail?: string | null;
    videoDuration?: number | null;
    publishedAt?: string | Date | null;
    createdAt?: string | Date | null;
  };
}

function VideoObjectSchema({ article }: VideoSchemaProps) {
  if (!article.videoUrl) return null;

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `PT${hours > 0 ? hours + "H" : ""}${minutes}M${secs}S`;
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": article.title,
    "description": article.excerpt || article.title,
    "contentUrl": article.videoUrl,
    "thumbnailUrl": article.videoThumbnail || undefined,
    "duration": article.videoDuration ? formatDuration(article.videoDuration) : undefined,
    "uploadDate": article.publishedAt 
      ? new Date(article.publishedAt).toISOString() 
      : article.createdAt 
        ? new Date(article.createdAt).toISOString() 
        : undefined,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

interface TranscriptSectionProps {
  transcript: string;
}

function TranscriptSection({ transcript }: TranscriptSectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="mt-8">
      <Separator className="mb-6" />
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button 
            variant="outline" 
            className="w-full justify-between"
            data-testid="button-toggle-transcript"
          >
            <span className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Video Transcript
            </span>
            <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Card className="mt-4">
            <CardContent className="pt-4">
              <div 
                className="prose prose-sm dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: transcript }}
                data-testid="content-transcript"
              />
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

interface ContactPanelProps {
  articleTitle: string;
  companies?: Company[];
  people?: Person[];
}

function ContactPanel({ articleTitle, companies = [], people = [] }: ContactPanelProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [personModalOpen, setPersonModalOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<{ label: string; url: string; companyName?: string } | null>(null);
  const [selectedPerson, setSelectedPerson] = useState<{ name: string; jobTitle?: string; linkedinUrl: string } | null>(null);

  const companyLinks = companies
    .filter(company => company.website)
    .map(company => ({
      label: company.name,
      url: company.website!,
      companyName: company.name,
    }));

  const handleLinkClick = (link: { label: string; url: string; companyName?: string }) => {
    setSelectedLink(link);
    setModalOpen(true);
  };

  const handlePersonClick = (person: Person) => {
    setSelectedPerson({
      name: `${person.firstName} ${person.lastName}`,
      jobTitle: person.jobTitle || undefined,
      linkedinUrl: person.linkedin!,
    });
    setPersonModalOpen(true);
  };

  const handleProceed = () => {
    if (selectedLink) {
      window.open(selectedLink.url, '_blank', 'noopener,noreferrer');
    }
    setModalOpen(false);
  };

  const handlePersonProceed = () => {
    if (selectedPerson) {
      window.open(selectedPerson.linkedinUrl, '_blank', 'noopener,noreferrer');
    }
    setPersonModalOpen(false);
  };

  const hasCompanies = companyLinks.length > 0;
  const hasPeople = people.filter(p => p.linkedin).length > 0;

  if (!hasCompanies && !hasPeople) {
    return null;
  }

  return (
    <>
      <Card className="mt-8" data-testid="contact-panel">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Enquiry Centre
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Connect with the companies and people featured in this article
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {hasCompanies && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Companies
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {companyLinks.map((link, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    className="justify-between"
                    onClick={() => handleLinkClick(link)}
                    data-testid={`button-company-link-${index}`}
                  >
                    <span className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      {link.label}
                    </span>
                    <ExternalLink className="h-4 w-4 text-muted-foreground" />
                  </Button>
                ))}
              </div>
            </div>
          )}

          {hasPeople && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <User className="h-4 w-4" />
                People
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {people
                  .filter(person => person.linkedin)
                  .map((person, index) => (
                    <Button
                      key={person.id}
                      variant="outline"
                      className="justify-between"
                      onClick={() => handlePersonClick(person)}
                      data-testid={`button-person-link-${index}`}
                    >
                      <span className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <span className="truncate">
                          {person.firstName} {person.lastName}
                          {person.jobTitle && (
                            <span className="text-muted-foreground ml-1 text-xs">
                              - {person.jobTitle}
                            </span>
                          )}
                        </span>
                      </span>
                      <FaLinkedin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    </Button>
                  ))}
              </div>
            </div>
          )}

          <Separator />

          <div className="bg-accent/30 rounded-md p-4">
            <div className="flex items-start gap-3">
              <Award className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-sm">Interested in featuring your company?</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Contact our sales team to learn about sponsorship and premium placement opportunities.
                </p>
                <Button variant="default" size="sm" className="mt-3" data-testid="button-contact-sales">
                  <Mail className="h-4 w-4 mr-2" />
                  Contact Sales
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {selectedLink && (
        <EnquiryModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          link={selectedLink}
          onProceed={handleProceed}
        />
      )}

      {selectedPerson && (
        <PersonEnquiryModal
          isOpen={personModalOpen}
          onClose={() => setPersonModalOpen(false)}
          person={selectedPerson}
          onProceed={handlePersonProceed}
        />
      )}
    </>
  );
}

interface ArticleRadarCardProps {
  articleId: string;
}

function ArticleRadarCard({ articleId }: ArticleRadarCardProps) {
  const { data } = useQuery<{ config: { id: string; title: string; slug: string; productName: string; productDescription?: string | null } } | null>({
    queryKey: [`/api/public/radars/by-article/${articleId}`],
    enabled: !!articleId,
    staleTime: 5 * 60_000,
  });

  if (!data?.config) return null;
  const { config } = data;

  return (
    <Card className="mt-8 border-primary/30 bg-gradient-to-br from-primary/5 to-background" data-testid={`radar-card-article-${articleId}`}>
      <CardContent className="pt-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-3">
            <Target className="h-8 w-8 text-primary shrink-0 mt-0.5" />
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-xs font-semibold text-primary uppercase tracking-wider">Radar</span>
                <Badge variant="secondary" className="text-xs">Free evaluation</Badge>
              </div>
              <h3 className="font-semibold">{config.title}</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                {config.productDescription
                  ? config.productDescription.slice(0, 120) + (config.productDescription.length > 120 ? "…" : "")
                  : `Evaluate ${config.productName} against your specific requirements`}
              </p>
            </div>
          </div>
          <Button asChild className="shrink-0" data-testid={`button-article-radar-${config.id}`}>
            <Link href={`/radar/${config.slug}/evaluate`}>
              Start Evaluation
              <ChevronRight className="ml-1.5 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function ArticleLegacyRedirectPage() {
  const { slug } = useParams<{ slug: string }>();
  const [, navigate] = useLocation();

  const { data: article } = useQuery<ArticleWithRelations>({
    queryKey: [`/api/articles/slug/${slug}`],
    enabled: !!slug,
  });

  useEffect(() => {
    if (article) {
      navigate(getArticleUrl(article), { replace: true });
    }
  }, [article, navigate]);

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center">
      <Skeleton className="h-8 w-64 mb-4" />
      <Skeleton className="h-4 w-40" />
    </div>
  );
}

interface ArticleViewProps {
  article: ArticleWithRelations;
  galleryImages: GalleryImage[];
  otherArticles: Article[];
  factsData?: { facts: FactItem[] };
  factsLoading?: boolean;
  /** Optional banner rendered above the article header (e.g. preview mode notice) */
  banner?: ReactNode;
  /** Optional slot rendered after the article content, before the site footer */
  footerSlot?: ReactNode;
}

interface FactItem {
  fact: string;
  topic: string;
}

/**
 * ArticleView renders the complete article page layout given pre-fetched data.
 * Used by both the public ArticlePage and the private PreviewPage so both
 * render identically from the same component tree.
 */
export function ArticleView({ article, galleryImages, otherArticles, factsData, factsLoading = false, banner, footerSlot }: ArticleViewProps) {
  const galleryHeroImage = galleryImages.find(img => img.isHero);
  const nonHeroImages = galleryImages.filter(img => !img.isHero).sort((a, b) => a.sortOrder - b.sortOrder);
  const heroImageUrl = article.featuredImage || galleryHeroImage?.asset?.objectPath || null;

  const getImageUrl = (image: GalleryImage | undefined, presetName?: string) => {
    if (!image?.asset) return "";
    if (presetName && image.variants?.length) {
      const variant = image.variants.find(v => v.preset?.name === presetName);
      if (variant) return variant.objectPath;
    }
    return image.asset.objectPath || "";
  };

  const splitContentForGallery = (content: string) => {
    if (!content || nonHeroImages.length === 0) return { before: content, after: "" };
    const paragraphs = content.split(/<\/p>/gi);
    if (paragraphs.length <= 2) return { before: content, after: "" };
    const midPoint = Math.ceil(paragraphs.length / 2);
    const before = paragraphs.slice(0, midPoint).join("</p>") + "</p>";
    const after = paragraphs.slice(midPoint).join("</p>");
    return { before, after };
  };

  const { before: contentBefore, after: contentAfter } = article.content
    ? splitContentForGallery(plainTextToHtml(article.content))
    : { before: "", after: "" };

  const brandColorMap: Record<string, { bg: string; text: string; border: string }> = {
    emerald: { bg: "bg-emerald-100 dark:bg-emerald-900/30", text: "text-emerald-700 dark:text-emerald-400", border: "border-emerald-300 dark:border-emerald-700" },
    blue:    { bg: "bg-blue-100 dark:bg-blue-900/30",    text: "text-blue-700 dark:text-blue-400",    border: "border-blue-300 dark:border-blue-700" },
    violet:  { bg: "bg-violet-100 dark:bg-violet-900/30", text: "text-violet-700 dark:text-violet-400", border: "border-violet-300 dark:border-violet-700" },
    slate:   { bg: "bg-slate-100 dark:bg-slate-800",     text: "text-slate-700 dark:text-slate-300",  border: "border-slate-300 dark:border-slate-600" },
  };

  // Inject Open Graph meta tags for social sharing
  useEffect(() => {
    const ogData: Record<string, string> = {
      "og:type": "article",
      "og:title": article.title,
      "og:description": article.excerpt || article.title,
      ...(heroImageUrl ? { "og:image": heroImageUrl } : {}),
      "twitter:card": heroImageUrl ? "summary_large_image" : "summary",
      "twitter:title": article.title,
      "twitter:description": article.excerpt || article.title,
      ...(heroImageUrl ? { "twitter:image": heroImageUrl } : {}),
    };
    const added: HTMLMetaElement[] = [];
    Object.entries(ogData).forEach(([property, content]) => {
      const attr = property.startsWith("twitter:") ? "name" : "property";
      let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${property}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, property);
        document.head.appendChild(el);
        added.push(el);
      }
      el.content = content;
    });
    const prevTitle = document.title;
    document.title = `${article.title} | Future Manufacturing`;
    return () => {
      document.title = prevTitle;
      added.forEach(el => el.remove());
    };
  }, [article.title, article.excerpt, heroImageUrl]);

  return (
    <ScrollArea className="h-screen">
      <VideoObjectSchema article={article} />
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <div className="max-w-7xl mx-auto px-4 pt-4 w-full">
          <PageBreadcrumb
            items={[
              { label: "Articles", href: "/articles" },
              { label: article.title },
            ]}
          />
        </div>
        {banner}
        <div className="flex-1 max-w-7xl mx-auto px-4 py-4 lg:py-8">

          <div className="lg:flex lg:gap-10">
            <article className="lg:flex-1 min-w-0">
              {article.videoUrl && article.videoProvider ? (
                <div className="mb-6">
                  <VideoFacade
                    videoUrl={article.videoUrl}
                    provider={article.videoProvider}
                    thumbnail={article.videoThumbnail || undefined}
                    title={article.title}
                    duration={article.videoDuration || undefined}
                    className="rounded-lg"
                    autoPlay
                  />
                </div>
              ) : heroImageUrl ? (
                <div className="relative w-full aspect-video overflow-hidden rounded-lg mb-6">
                  <img
                    src={heroImageUrl}
                    alt={article.title}
                    className="w-full h-full object-cover"
                    data-testid="img-article-hero"
                  />
                </div>
              ) : null}

              <div className="flex flex-wrap items-center gap-3 text-muted-foreground mb-4">
                {article.contentType === "video" && (
                  <Badge variant="default" className="bg-red-600 hover:bg-red-700">
                    <Play className="h-3 w-3 mr-1" />
                    Video
                  </Badge>
                )}
                {article.contentType === "mixed" && (
                  <Badge variant="default" className="bg-purple-600 hover:bg-purple-700">
                    <Play className="h-3 w-3 mr-1" />
                    Video Article
                  </Badge>
                )}
                <Badge variant="secondary">Technology</Badge>
                {article.videoDuration && (
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span className="text-sm">{Math.floor(article.videoDuration / 60)}:{(article.videoDuration % 60).toString().padStart(2, '0')}</span>
                  </div>
                )}
                {article.createdAt && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    <span className="text-sm">{format(new Date(article.createdAt), "MMMM d, yyyy")}</span>
                  </div>
                )}
              </div>

              <h1 className="text-2xl md:text-4xl font-bold mb-4" data-testid="text-article-title">
                {article.title}
              </h1>

              {article.brandTags && article.brandTags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4" data-testid="brand-tags">
                  {article.brandTags.map(tag => {
                    const colors = brandColorMap[tag.color] ?? brandColorMap.slate;
                    return (
                      <span
                        key={tag.slug}
                        className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${colors.bg} ${colors.text} ${colors.border}`}
                        data-testid={`brand-tag-${tag.slug}`}
                      >
                        {tag.shortName}
                      </span>
                    );
                  })}
                </div>
              )}

              {article.excerpt && (
                <p className="text-lg text-muted-foreground mb-4" data-testid="text-article-excerpt">
                  {article.excerpt}
                </p>
              )}

              <div className="mb-6 flex flex-wrap items-center gap-4">
                <ShareButtons title={article.title} url={getArticleUrl(article)} />
                <AddToShortListButton
                  item={{
                    type: "article",
                    articleId: article.id,
                    title: article.title,
                    category: "Article",
                    image: article.featuredImage || undefined,
                    shortlistButtonText: article.shortlistButtonText || undefined,
                    productType: article.productType || undefined,
                    technologyType: article.technologyType || undefined,
                  }}
                />
              </div>

              <Separator className="mb-6" />

              <InsightProvider articleSlug={article.slug} articleTitle={article.title}>
                {contentBefore && (
                  <HighlightedHTMLContent
                    html={contentBefore}
                    className="prose prose-lg dark:prose-invert max-w-none"
                    data-testid="content-article-body-1"
                  />
                )}

                {nonHeroImages.length > 0 && (
                  <ImageSlider images={nonHeroImages} getImageUrl={getImageUrl} />
                )}

                {contentAfter && (
                  <HighlightedHTMLContent
                    html={contentAfter}
                    className="prose prose-lg dark:prose-invert max-w-none"
                    data-testid="content-article-body-2"
                  />
                )}
              </InsightProvider>

              {article.transcript && (
                <TranscriptSection transcript={article.transcript} />
              )}

              {(article.companies?.length || article.people?.length) && (
                <>
                  <Separator className="my-8" />

                  <section className="space-y-4">
                    {article.companies && article.companies.length > 0 && (
                      <div className="space-y-2">
                        <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          Related Companies
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {article.companies.map((company) => (
                            <Badge key={company.id} variant="secondary" data-testid={`badge-company-${company.id}`}>
                              {company.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {article.people && article.people.length > 0 && (
                      <div className="space-y-2">
                        <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                          <User className="h-4 w-4" />
                          Related People
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {article.people.map((person) => (
                            <Badge key={person.id} variant="secondary" data-testid={`badge-person-${person.id}`}>
                              {person.firstName} {person.lastName}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </section>
                </>
              )}

              <ContactPanel articleTitle={article.title} companies={article.companies} people={article.people} />
              <ArticleRadarCard articleId={article.id} />
              <CtaPanel contentType="article" contentId={article.id} className="mt-8" />
            </article>

            <aside className="hidden lg:block lg:w-80 flex-shrink-0 space-y-6">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Newspaper className="h-4 w-4" />
                    Latest News
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {otherArticles.map((item) => (
                    <Link
                      key={item.id}
                      href={getArticleUrl(item)}
                      className="block group"
                      data-testid={`link-sidebar-article-${item.id}`}
                    >
                      <div className="flex gap-3">
                        {item.featuredImage && (
                          <div className="w-16 h-16 flex-shrink-0 rounded-md overflow-hidden">
                            <img
                              src={item.featuredImage}
                              alt={item.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <h4 className="text-sm font-medium line-clamp-2 group-hover:text-primary transition-colors">
                            {item.title}
                          </h4>
                          {item.createdAt && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {format(new Date(item.createdAt), "MMM d, yyyy")}
                            </p>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                  {otherArticles.length === 0 && (
                    <p className="text-sm text-muted-foreground">No other articles available.</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Lightbulb className="h-4 w-4" />
                    Did You Know?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {factsLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      <span className="ml-2 text-sm text-muted-foreground">Generating facts...</span>
                    </div>
                  ) : factsData?.facts?.length ? (
                    factsData.facts.map((item, idx) => (
                      <div key={idx} className="space-y-1" data-testid={`fact-item-${idx}`}>
                        <Badge variant="secondary" className="text-xs">{item.topic}</Badge>
                        <p className="text-sm text-muted-foreground">{item.fact}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No facts available for this article.</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Tag className="h-4 w-4" />
                    Featured Categories
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="hover-elevate cursor-pointer">Technology</Badge>
                    <Badge variant="outline" className="hover-elevate cursor-pointer">Industry 4.0</Badge>
                    <Badge variant="outline" className="hover-elevate cursor-pointer">Manufacturing</Badge>
                    <Badge variant="outline" className="hover-elevate cursor-pointer">Supply Chain</Badge>
                    <Badge variant="outline" className="hover-elevate cursor-pointer">Sustainability</Badge>
                    <Badge variant="outline" className="hover-elevate cursor-pointer">Innovation</Badge>
                  </div>
                </CardContent>
              </Card>
            </aside>
          </div>
        </div>
        {footerSlot}
        <SiteFooter />
      </div>
    </ScrollArea>
  );
}

export default function ArticlePage() {
  const { slug } = useParams();

  const { data: article, isLoading } = useQuery<ArticleWithRelations>({
    queryKey: [`/api/articles/slug/${slug}`],
    enabled: !!slug,
  });

  const { data: galleryImages = [] } = useQuery<GalleryImage[]>({
    queryKey: [`/api/articles/${article?.id}/gallery`],
    enabled: !!article?.id,
  });

  const { data: latestArticles = [] } = useQuery<Article[]>({
    queryKey: ["/api/articles/published"],
  });

  const { data: factsData, isLoading: factsLoading } = useQuery<{ facts: FactItem[] }>({
    queryKey: ['/api/articles', article?.id, 'facts'],
    enabled: !!article?.id,
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
  });

  const otherArticles = latestArticles.filter(a => a.id !== article?.id).slice(0, 4);

  // Track scroll depth + time on page for engagement analytics
  useArticleEngagement(article?.id);

  // Set article ID meta tag so the page view tracker can associate this view with the article
  useEffect(() => {
    if (!article?.id) return;
    let meta = document.querySelector<HTMLMetaElement>('meta[name="fm-article-id"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "fm-article-id";
      document.head.appendChild(meta);
    }
    meta.content = article.id;
    return () => { meta!.content = ""; };
  }, [article?.id]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <div className="max-w-7xl mx-auto px-4 pt-4 w-full">
          <PageBreadcrumb items={[{ label: "Articles", href: "/articles" }, { label: "Loading…" }]} />
        </div>
        <div className="flex-1 max-w-7xl mx-auto px-4 py-8 lg:flex lg:gap-8">
          <div className="lg:flex-1">
            <Skeleton className="w-full aspect-video rounded-lg mb-6" />
            <Skeleton className="h-12 w-3/4 mb-4" />
            <Skeleton className="h-6 w-full mb-2" />
            <Skeleton className="h-6 w-full mb-2" />
            <Skeleton className="h-6 w-2/3" />
          </div>
          <div className="hidden lg:block lg:w-80">
            <Skeleton className="h-64 rounded-lg mb-4" />
            <Skeleton className="h-48 rounded-lg" />
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
          <p className="text-muted-foreground mb-6">The article you're looking for doesn't exist.</p>
          <Button asChild>
            <Link href="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <ArticleView
      article={article}
      galleryImages={galleryImages}
      otherArticles={otherArticles}
      factsData={factsData}
      factsLoading={factsLoading}
    />
  );
}
